import React from 'react'
import { useTheme } from '../themes/styled'
import Text from 'particles/Text'
import Icon, { IconColor } from 'particles/Icon'
import { getAlertContentColor, StyledConfirmationAlert, StyledButton, StyledButtonContainer } from './style'
import { useIsMobileView } from 'hooks'

export type AlertContentType = 'positive' | 'warning' | 'error' | 'info'

interface AlertButtonsProps {
    contentType: AlertContentType
    primaryButtonText?: string
    onClickPrimaryButton?: () => void
    secondaryButtonText?: string
    onClickSecondaryButton?: () => void
}

export const AlertButtons: React.FC<AlertButtonsProps> = ({
    contentType,
    primaryButtonText,
    onClickPrimaryButton,
    secondaryButtonText,
    onClickSecondaryButton,
}) => {
    return (
        <StyledButtonContainer>
            {secondaryButtonText && (
                <StyledButton
                    data-testid="test-alert-secondary-button"
                    contentType={contentType}
                    onClick={onClickSecondaryButton}
                >
                    {secondaryButtonText}
                </StyledButton>
            )}
            {primaryButtonText && (
                <StyledButton
                    data-testid="test-alert-primary-button"
                    contentType={contentType}
                    onClick={onClickPrimaryButton}
                >
                    {primaryButtonText}
                </StyledButton>
            )}
        </StyledButtonContainer>
    )
}

interface ConfirmationAlertProps {
    contentType: AlertContentType
    title: string
    subText?: string
    primaryButtonText?: string
    onClickPrimaryButton?: () => void
    secondaryButtonText?: string
    onClickSecondaryButton?: () => void
}

const ConfirmationAlert: React.FC<ConfirmationAlertProps> = ({
    contentType,
    title,
    subText,
    primaryButtonText,
    onClickPrimaryButton,
    secondaryButtonText,
    onClickSecondaryButton,
}) => {
    const theme = useTheme()
    const isMobileView = useIsMobileView()
    return (
        <StyledConfirmationAlert contentType={contentType}>
            <div className="icon-content-container">
                {!isMobileView && (
                    <>
                        {contentType === 'positive' && (
                            <div className="icon-container">
                                <Icon
                                    name={'checkCircle'}
                                    size={'40px'}
                                    color={getAlertContentColor('icon', contentType, theme) as IconColor}
                                />
                            </div>
                        )}
                        {contentType === 'error' && (
                            <div className="icon-container">
                                <Icon
                                    name={'xCircle'}
                                    size={'40px'}
                                    color={getAlertContentColor('icon', contentType, theme) as IconColor}
                                />
                            </div>
                        )}
                        {contentType === 'warning' && (
                            <div className="icon-container">
                                <Icon
                                    name={'warning'}
                                    size={'40px'}
                                    color={getAlertContentColor('icon', contentType, theme) as IconColor}
                                />
                            </div>
                        )}
                        {contentType === 'info' && (
                            <div className="icon-container">
                                <Icon
                                    name={'info'}
                                    size={'40px'}
                                    color={getAlertContentColor('icon', contentType, theme) as IconColor}
                                />
                            </div>
                        )}
                    </>
                )}
                <div className="text-container">
                    <Text className="title" scale={'H4'}>
                        {title}
                    </Text>
                    {subText && (
                        <Text className="subText" scale={'bodyS500'} color={'primary'}>
                            {subText}
                        </Text>
                    )}
                </div>
            </div>
            <AlertButtons
                contentType={contentType}
                primaryButtonText={primaryButtonText}
                onClickPrimaryButton={onClickPrimaryButton}
                secondaryButtonText={secondaryButtonText}
                onClickSecondaryButton={onClickSecondaryButton}
            />
        </StyledConfirmationAlert>
    )
}

export default ConfirmationAlert
